'use client'

import { motion } from 'framer-motion'
import { Link } from 'react-router-dom'
import '@/styles/bauhaus.css'
import { useLanguage } from '@/i18n/LanguageProvider'
import { appLanguageToContentLocale } from '@/i18n/contentLocale'
import introData from './intro.json'
import eventsData from './events.json'

export default function BerlinPoetryFestivalEvents() {
  const { t, language } = useLanguage()

  const lang = appLanguageToContentLocale(language);

  // 直接遍历 slug 作为 key 的 eventsData
  const eventList = Object.entries(eventsData)
    .map(([eventId, data]) => ({ eventId, ...data }))
    .sort((a, b) => a.date.localeCompare(b.date));

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#1C1C1C] to-[#252525]">
      {/* 页面头部 */}
      <div className="pt-32 pb-16 relative bg-gradient-to-b from-[#1C1C1C] to-[#1C1C1C]">
        <div className="absolute inset-0 bg-grid-pattern opacity-5"></div>
        <div className="absolute w-full h-full bg-[radial-gradient(ellipse_at_center,_rgba(120,120,120,0.1)_0%,rgba(0,0,0,0)_70%)]"></div>
        
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          {/* 返回链接 */}
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="mb-8"
          >
            <Link 
              to="/Qiaodong/BPF"
              className="inline-flex items-center text-[#B9AE9A]/60 hover:text-[#B9AE9A] transition-colors duration-300 text-sm font-bauhaus"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              {t('bpf.back_to_qiaodong')}
            </Link>
          </motion.div>

          <div className="text-center mb-12">
            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="text-4xl md:text-5xl font-bauhaus text-white mb-6"
            >
              <span className="bg-clip-text text-transparent bg-gradient-to-r from-[#B9AE9A] to-[#8C4233]">
                {t('bpf.events.festival.title')}
              </span>
            </motion.h1>
          </div>
          
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="bg-[#1C1C1C]/30 backdrop-blur-sm border border-[#B9AE9A]/10 rounded-xl overflow-hidden"
          >
            <div className="p-8 md:p-12">
              <div className="space-y-6 max-w-5xl mx-auto">
                <p className="text-white/90 text-lg leading-relaxed text-left">
                  {introData.intro?.[lang] || introData.intro?.zh || ''}
                </p>
                {introData.sections?.map((section, idx) => (
                  <div key={section.key} className="mt-6">
                    <div className="text-[#B9AE9A] text-xl font-bauhaus mb-2">{section.title?.[lang] || section.title?.zh}</div>
                    <div className="text-white/80 text-lg leading-relaxed text-left">{section.content?.[lang] || section.content?.zh}</div>
                  </div>
                ))}
              </div>
            </div>
          </motion.div>
        </div>
      </div>

      {/* 主要内容 */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        
        {/* 活动时间表 */}
        <div className="mb-20">
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.6 }}
            className="text-3xl font-bauhaus text-white mb-12 text-center"
          >
            <span className="bg-clip-text text-transparent bg-gradient-to-r from-[#B9AE9A] to-[#8C4233]">
              {t('bpf.events.schedule_title')}
            </span>
          </motion.h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {eventList.map((event, index) => (
              <motion.div
                key={event.eventId}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.8 + index * 0.1 }}
              >
                <Link to={`/Qiaodong/BPF/events/${event.eventId}`} className="block">
                  <div className="bg-[#1C1C1C]/40 backdrop-blur-sm border border-[#B9AE9A]/20 rounded-xl overflow-hidden group hover:border-[#B9AE9A]/40 transition-all duration-300 hover:transform hover:scale-[1.02] cursor-pointer h-64">
                    <div className="p-8">
                      <div className="flex items-start space-x-6">
                        {/* 日期 */}
                        <div className="flex-shrink-0">
                          <div className="w-20 h-20 bg-gradient-to-r from-[#B9AE9A] to-[#8C4233] rounded-xl flex items-center justify-center mb-2">
                            <span className="text-3xl font-bauhaus text-white">{event.date.slice(8,10)}</span>
                          </div>
                          <div className="text-center">
                            <div className="text-3xl text-white/60">{event.date.slice(5,7)}</div>
                          </div>
                        </div>
                        {/* 活动详情 */}
                        <div className="flex-1">
                          <h3 className="text-xl font-bauhaus text-white mb-3 group-hover:text-[#B9AE9A] transition-colors duration-300">
                            {event[lang]?.title || event.zh?.title}
                          </h3>
                          <div className="text-sm text-[#B9AE9A] mb-2 font-mono">{event.eventId}</div>
                          <div className="mt-4 flex items-center text-[#B9AE9A] group-hover:text-white transition-colors duration-300">
                            <span className="text-sm font-bauhaus mr-2">{event[lang]?.location || event.zh?.location}</span>
                          </div>
                          <div className="mt-2 text-sm text-white/60">
                            {event[lang]?.guests || event.zh?.guests}
                          </div>
                          <div className="mt-2 text-xs text-white/40">
                            {event[lang]?.keywords || event.zh?.keywords}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </div>
  )
} 